import { useQuery } from '@tanstack/react-query';
import { getDocs, orderBy, query } from 'firebase/firestore';
import { useMemo } from 'react';

import { useAuth } from '@/app/auth';
import { sensorsCollection } from '@/lib/firestorePaths';

import type { Sensor } from '@/features/homeAutomation/types/sensor';

import { POLL_SECONDS_MAX, POLL_SECONDS_MIN } from './config';

export const sensorsWidgetQueryKey = (homeId: string | null) => ['widget', 'sensors', homeId] as const;

interface Params {
  homeId: string | null;
  sensorIds: string[];
  pollSeconds: number;
}

export default function useSensorsWidgetData({ homeId, sensorIds, pollSeconds }: Params) {
  const { status } = useAuth();
  const enabled = status === 'authorized' && !!homeId;
  const refetchMs = Math.min(Math.max(pollSeconds, POLL_SECONDS_MIN), POLL_SECONDS_MAX) * 1000;

  const result = useQuery<Sensor[]>({
    queryKey: sensorsWidgetQueryKey(homeId),
    queryFn: async () => {
      const snap = await getDocs(query(sensorsCollection(homeId!), orderBy('name')));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
    enabled,
    staleTime: refetchMs / 2,
    refetchInterval: enabled ? refetchMs : false,
  });

  // Filter to selected ids (empty = all). Drops disabled sensors so the widget
  // never shows administratively-off sensors.
  const filtered = useMemo(() => {
    const all = result.data ?? [];
    const visible = all.filter((s) => s.enabled);
    if (sensorIds.length === 0) return visible;
    const wanted = new Set(sensorIds);
    return visible.filter((s) => wanted.has(s.id));
  }, [result.data, sensorIds]);

  return { ...result, sensors: filtered };
}
